import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Avatar from 'antd/lib/avatar';
import Divider from 'antd/lib/divider';
import Typography from 'antd/lib/typography';
import Text from 'antd/lib/typography/Text';
import { PullRequestOutlined, TagOutlined } from '@ant-design/icons';
import { RootState } from '@/reducers/reducers';
import { FeatureItem, ReleaseStepType } from '@/types';
import { FetchableEntityType } from '@/entityType';
import { AddFixRequestWorkItemData } from '@/actions/releaseStep';
import { tryFetchEntity } from '@/actions/entities';
import { generateBackwardsCompatibleReleaseStepRunId } from '@/reducers/releaseStep';
import {
    selectAllTicketOnlyItems,
    selectFeatureManagementIntegration,
    selectIsReleaseStepPreviouslyFetched,
    selectVCSIntegration,
} from '@/selectors/selectors';
import { selectAllOpenPRs, selectCommitsOnlyOnWorkingBranch } from '@/selectors/memoizedSelectors';
import { codeIsCommit } from '@/helpers';
import { antGrey, getAvatarColorForName } from '@/components/styles/globalStyles';
import IntegrationIcon from '@/components/icons/IntegrationIcon';
import commitIcon from '../../../assets/commit-icon.svg';

enum FixOptionGroup {
    OpenPRs,
    WorkOnWorkingBranch,
    Tickets,
}

export const useFixWorkItemCandidates = (stepRunId: string): FeatureItem[] => {
    const openPRs = useSelector((state: RootState) => selectAllOpenPRs(state, stepRunId));
    const commits = useSelector((state: RootState) => selectCommitsOnlyOnWorkingBranch(state, stepRunId));
    const tickets = useSelector((state: RootState) => selectAllTicketOnlyItems(state, stepRunId));
    return React.useMemo(
        () => [...openPRs, ...commits, ...tickets].filter((item) => !item.isItemDone),
        [openPRs, commits, tickets],
    );
};

// Maps a selected feature item to the work-item payload (PR number for PRs, ticket key/url for tickets).
export const toAddFixRequestWorkItemData = (item: FeatureItem): AddFixRequestWorkItemData => ({
    id: item.id,
    pullRequestNumber: item.code && !codeIsCommit(item.code) ? parseInt(item.code.identifier) : undefined,
    ticketKey: item.ticket?.identifier,
    ticketUrl: item.ticket?.url,
});

interface WorkItemSelectProps {
    orgId: string;
    appId: string;
    releaseId: string;
    stepRunId: string;
    releaseStepType?: ReleaseStepType;
    multiple?: boolean;
    disabled?: boolean;
    placeholder?: string;
    extraTicket?: { id: string; identifier: string; url: string; title?: string; owner?: string };
    lockedItemId?: string;
    excludeDoneItems?: boolean;
    value?: string | string[];
    onChange?: (value: string | string[]) => void;
}

const WorkItemSelect: React.FC<WorkItemSelectProps> = ({
    orgId,
    appId,
    releaseId,
    stepRunId,
    releaseStepType = ReleaseStepType.FeatureReadiness,
    multiple,
    disabled,
    placeholder = 'Select',
    extraTicket,
    lockedItemId,
    excludeDoneItems,
    value,
    onChange,
}) => {
    const dispatch = useDispatch();
    const openPRs = useSelector((state: RootState) => selectAllOpenPRs(state, stepRunId));
    const commitsOnlyOnWorkingBranch = useSelector((state: RootState) =>
        selectCommitsOnlyOnWorkingBranch(state, stepRunId),
    );
    const ticketOnlyItems = useSelector((state: RootState) => selectAllTicketOnlyItems(state, stepRunId));
    // Only the triage flow (excludeDoneItems) drops items already done — a fix pulls work into the release, so there's
    // nothing to do for done code/tickets. The standard "Add a fix" flow keeps its original behavior (all items).
    const selectablePRs = excludeDoneItems ? openPRs.filter((item) => !item.isItemDone) : openPRs;
    const selectableCommits =
        excludeDoneItems ? commitsOnlyOnWorkingBranch.filter((item) => !item.isItemDone) : commitsOnlyOnWorkingBranch;
    const selectableTicketOnlyItems =
        excludeDoneItems ? ticketOnlyItems.filter((item) => !item.isItemDone) : ticketOnlyItems;
    const vcsIntegration = useSelector((state: RootState) => selectVCSIntegration(state, appId));
    const featureManagementIntegration = useSelector((state: RootState) =>
        selectFeatureManagementIntegration(state, appId),
    );
    const isStepFetched = useSelector((state: RootState) =>
        selectIsReleaseStepPreviouslyFetched(state, releaseId, releaseStepType),
    );

    // Load the release step's work items if they aren't already in state (e.g. opened outside the step's page).
    React.useEffect(() => {
        if (!isStepFetched && releaseId) {
            dispatch(
                tryFetchEntity(
                    FetchableEntityType.ReleaseStep,
                    { orgId, appId, releaseId, releaseStepType },
                    generateBackwardsCompatibleReleaseStepRunId(releaseId, releaseStepType),
                ),
            );
        }
    }, [isStepFetched, releaseId, orgId, appId, releaseStepType]);

    // Render the off-release source ticket as a regular ticket option (unless it's already a release ticket).
    const extraTicketItem: FeatureItem | undefined =
        extraTicket && !selectableTicketOnlyItems.some((item) => item.ticket?.identifier === extraTicket.identifier) ?
            ({
                id: extraTicket.id,
                ticket: {
                    identifier: extraTicket.identifier,
                    url: extraTicket.url,
                    description: extraTicket.title ?? '',
                    owner: extraTicket.owner,
                },
            } as unknown as FeatureItem)
        :   undefined;

    const groups = [
        {
            key: FixOptionGroup.OpenPRs,
            label: 'Open PRs',
            options: selectablePRs,
            emptyState: 'No open PRs',
        },
        {
            key: FixOptionGroup.WorkOnWorkingBranch,
            label: 'Work on the working branch',
            options: selectableCommits,
            emptyState: 'No work on the working branch',
        },
        {
            key: FixOptionGroup.Tickets,
            label: 'Tickets',
            options: extraTicketItem ? [...selectableTicketOnlyItems, extraTicketItem] : selectableTicketOnlyItems,
            emptyState: 'No ticket-only items',
        },
    ];

    // A fix request maps to a single feature readiness item, so allow at most one code (PR/commit) and one ticket
    const selectedIds =
        Array.isArray(value) ? value
        : value ? [value]
        : [];
    const codeIds = new Set([...selectablePRs, ...selectableCommits].map((item) => item.id));
    const ticketIds = new Set(
        (extraTicketItem ? [...selectableTicketOnlyItems, extraTicketItem] : selectableTicketOnlyItems).map(
            (item) => item.id,
        ),
    );
    const hasCodeSelected = selectedIds.some((id) => codeIds.has(id));
    const hasTicketSelected = selectedIds.some((id) => ticketIds.has(id));

    const { Option, OptGroup } = Select;

    const ownerName = (item: FeatureItem): string =>
        (!!item.ticket && !item.code ? (item.ticket.owner ?? '') : (item.code?.author ?? '')) || '';

    const itemText = (item: FeatureItem): string => {
        if (!!item.ticket && !item.code) {
            return item.ticket.description ?? '';
        }
        return (codeIsCommit(item.code) ? item.code?.message : item.code?.title) ?? '';
    };

    const itemSearchLabel = (item: FeatureItem): string => {
        if (!item.code && !!item.ticket) {
            return `${item.ticket.identifier} ${item.ticket.description}`;
        }
        return codeIsCommit(item.code) ?
                `${item.code.identifier} ${item.code.message}`
            :   `${item.code?.identifier} ${(item.code as { title?: string })?.title}`;
    };

    return (
        <Select
            mode={multiple ? 'multiple' : undefined}
            placeholder={placeholder}
            optionFilterProp="label"
            showSearch
            disabled={disabled}
            value={value}
            onChange={onChange}
        >
            {groups.map((group) => (
                <OptGroup
                    key={group.key}
                    label={
                        <Space style={{ paddingTop: 4 }} align="center">
                            {group.label}
                            {group.key === FixOptionGroup.Tickets ?
                                featureManagementIntegration && (
                                    <IntegrationIcon integrationId={featureManagementIntegration.id} />
                                )
                            :   vcsIntegration && <IntegrationIcon integrationId={vcsIntegration.id} />}
                        </Space>
                    }
                >
                    {group.options.length === 0 ?
                        <Option value={undefined} key={group.emptyState} disabled>
                            <Text type="secondary" disabled>
                                {group.emptyState}
                            </Text>
                        </Option>
                    :   group.options.map((item) => (
                            <Option
                                value={item.id}
                                key={`${item.id}-${group.key}`}
                                label={itemSearchLabel(item)}
                                disabled={
                                    item.id === lockedItemId ||
                                    (multiple &&
                                        !selectedIds.includes(item.id) &&
                                        (group.key === FixOptionGroup.Tickets ? hasTicketSelected : hasCodeSelected))
                                }
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        justifyContent: 'flex-start',
                                    }}
                                >
                                    {group.key === FixOptionGroup.OpenPRs && (
                                        <>
                                            <PullRequestOutlined />
                                            <Typography.Text>#{item.code?.identifier}</Typography.Text>
                                            <Typography.Text code>
                                                {(item.code as { baseBranch?: string })?.baseBranch}
                                            </Typography.Text>
                                        </>
                                    )}
                                    {group.key === FixOptionGroup.WorkOnWorkingBranch && (
                                        <>
                                            <img src={commitIcon} />
                                            <Typography.Text code>{item.code?.identifier}</Typography.Text>
                                        </>
                                    )}
                                    {group.key === FixOptionGroup.Tickets && (
                                        <>
                                            <TagOutlined />
                                            <Typography.Text code>{item.ticket?.identifier}</Typography.Text>
                                        </>
                                    )}
                                    <Avatar
                                        style={{
                                            backgroundColor: getAvatarColorForName(ownerName(item)),
                                            fontSize: 12,
                                            flexShrink: 0,
                                        }}
                                        size={20}
                                    >
                                        {ownerName(item).charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Typography.Text style={{ color: antGrey.grey6 }}>
                                        {ownerName(item)}
                                    </Typography.Text>
                                    <Divider
                                        type="vertical"
                                        style={{
                                            height: 20,
                                            borderWidth: '1px',
                                            borderColor: antGrey.grey5,
                                            marginLeft: '4px',
                                            marginRight: '4px',
                                        }}
                                    />
                                    <Typography.Text
                                        ellipsis={{ tooltip: { title: itemText(item), placement: 'right' } }}
                                        style={{ flex: 1, verticalAlign: 'middle' }}
                                    >
                                        {itemText(item)}
                                    </Typography.Text>
                                </div>
                            </Option>
                        ))
                    }
                </OptGroup>
            ))}
        </Select>
    );
};

export default WorkItemSelect;
